import React, { useState } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import HotelController, { DeviceType } from '../../controller/HotelController';
interface HotelDeviceSummaryTableProps {
  deviceList: DeviceType[];
}

const HotelDeviceSummaryTable = ({ deviceList }: HotelDeviceSummaryTableProps) => {
  const occasionalDeviceCheckIn: DeviceType[] = [];
  const occasionalDeviceUnCheckIn: DeviceType[] = [];
  const constantDeviceOn: DeviceType[] = [];
  const constantDeviceOff: DeviceType[] = [];
  const today = new Date();
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setHours(today.getHours() - 48);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  deviceList.forEach((device) => {
    const isOccasional = device.device_power === 0 || device.device_wifi_power === 0;
    const isConstant = device.device_power === 1 && device.device_wifi_power === 1;

    if (isOccasional) {
      device.device_status === 'checkin'
        ? occasionalDeviceCheckIn.push(device)
        : occasionalDeviceUnCheckIn.push(device);
    } else if (isConstant) {
      device.normal === 1 ? constantDeviceOn.push(device) : constantDeviceOff.push(device);
    }
  });
  // 로그기록 3일이상 없는경우
  const filteredDeviceList = deviceList.filter((device) => {
    const loggedAtDate = new Date(device.logged_at);
    return loggedAtDate < twoDaysAgo;
  });
  // 기기오류+로그기록 3일 이상 중복 제거
  const combinedDeviceList = [
    ...constantDeviceOff,
    ...filteredDeviceList.filter(
      (device) =>
        !constantDeviceOff.some((offDevice) => offDevice.user_token_id === device.user_token_id),
    ),
  ];

  const RoomStatusSection = ({
    title,
    totalCount,
    roomTypes,
    notice,
  }: {
    title: string;
    totalCount: number;
    roomTypes: any[];
    notice: string;
  }) => (
    <>
      <h2>{title}</h2>
      <div className="totalTableWrap">
        <div className="totalRommCount">
          <h3>객실수</h3>
          <p>{totalCount}</p>
        </div>
        <div className="roomTypeWrap">
          {roomTypes.map(({ label, count, names }) => (
            <div className={`roomTypeBox ${label === '끊김' && 'error'}`} key={label}>
              <div className="roomTypeCount">
                <h3>{label}</h3>
                <p>{count}</p>
              </div>
              <div className="roomTypeList">
                <h4>{names.length ? names.join(', ') : ''}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="noticeBox">
        <h4>{notice}</h4>
      </div>
    </>
  );
  const sections = [
    {
      title: '전원 또는 와이파이 비상시 설정 객실',
      totalCount: occasionalDeviceCheckIn.length + occasionalDeviceUnCheckIn.length,
      roomTypes: [
        {
          label: '입실수',
          count: occasionalDeviceCheckIn.length,
          names: occasionalDeviceCheckIn.map((d) => d.name),
        },
        {
          label: '공실수',
          count: occasionalDeviceUnCheckIn.length,
          names: occasionalDeviceUnCheckIn.map((d) => d.name),
        },
      ],
      notice: '입실 목록에 실제 입실한 객실이 표시되지 않으면 해당 객실의 기기 확인이 필요합니다.',
    },
    {
      title: '전원과 와이파이 모두 상시 설정 객실',
      totalCount: constantDeviceOn.length + constantDeviceOff.length,
      roomTypes: [
        {
          label: '작동중',
          count: constantDeviceOn.length,
          names: constantDeviceOn.map((d) => d.name),
        },
        {
          label: '끊김',
          count: constantDeviceOff.length,
          names: constantDeviceOff.map((d) => d.name),
        },
      ],
      notice: '끊김으로 표시된 객실의 전원 분리 여부, 와이파이 작동 여부를 확인해주세요.',
    },
  ].filter((section) => section.totalCount > 0);

  return (
    <>
      <Style>
        {sections.map((section) => (
          <RoomStatusSection key={section.title} {...section} />
        ))}
        {/* 기기확인 요청 */}
        {combinedDeviceList.length > 0 && (
          <div className="errorDeviceWrap">
            <div className={`errorTitle ${!isOpen && 'close'}`}>
              <img src={`${process.env.PUBLIC_URL}/svg/ic_exclamation.svg`} alt="ic" />
              <h4 style={{ fontSize: '18px' }}>기기 확인요청 총 {combinedDeviceList.length}개</h4>
              <button onClick={() => setIsOpen(!isOpen)}>
                자세히보기
                <img src={`${process.env.PUBLIC_URL}/svg/ic_arrow_right_white2.svg`} alt="ic" />
              </button>
            </div>
            {isOpen && (
              <div className="errorContent">
                {combinedDeviceList.map((data) => (
                  <div className="errorItem">
                    <h4>{data.name}</h4>
                    {new Date(data.logged_at) < twoDaysAgo && (
                      <p style={{ fontWeight: '700', color: 'orange' }}>48시간 사용기록 없음 </p>
                    )}
                    {data.device_is_charging === 0 && <p className="errorTime">충전 불량</p>}
                    {data.is_connected === 0 && (
                      <div className="errorItemWrap">
                        <p className="errorTime">{`인터넷 끊김 시간\n${moment(
                          data.loggedout_at,
                        ).format('YYYY/MM/DD')}\n${moment(data.loggedout_at).format(
                          'HH:mm:ss',
                        )}`}</p>
                      </div>
                    )}
                    {data.device_app_state === 'background' && (
                      <p className="errorTime">앱이 종료되었음</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="noticeBox errorBox">
              <h4>장시간 사용기록이 없는 객실은 확인이 필요합니다.</h4>
            </div>
          </div>
        )}
      </Style>
    </>
  );
};

export default HotelDeviceSummaryTable;

const Style = styled.div`
  h2 {
    margin-bottom: 4px;
    font-size: 16px;
    font-weight: 500;
    color: #000;
  }
  h3 {
    color: #333;
    font-size: 14px;
  }
  h4 {
    font-weight: 400;
    font-size: 14px;
  }
  p {
    color: #333;
    font-size: 16px;
    font-weight: 600;
  }

  .errorTitle {
    display: flex;

    button {
      display: flex;
      align-items: center;
      margin-left: auto;
      border-radius: 4px;
      color: #fff;
      font-size: 14px;

      img {
        transform: rotate(270deg);
        width: 24px;
      }
    }
  }
  .errorTitle.close {
    border-radius: 5px;
    button img {
      transform: rotate(90deg);
    }
  }

  .totalTableWrap {
    display: flex;
    justify-content: stretch;
    align-items: center;
    border: 1px solid #e2e2e2;
    border-radius: 10px;
  }
  .totalRommCount {
    width: 15%;
    text-align: center;
  }
  .roomTypeWrap {
    flex: 1;
    border: 1px solid #e2e2e2;
    margin: -1px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  .roomTypeBox {
    display: flex;
  }
  .roomTypeBox:nth-of-type(1) {
    border-bottom: 1px solid #e2e2e2;
  }
  .roomTypeBox.error {
    h3,
    p {
      color: #ff6060;
      font-weight: 700;
    }
  }
  .roomTypeCount {
    width: 15%;
    padding: 6px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #e2e2e2;
    text-align: center;
  }

  .roomTypeList {
    flex: 1;
    padding: 10px;
    word-break: keep-all;
    line-height: 24px;
  }
  .noticeBox {
    margin: 6px 0 15px 0;
    padding: 6px 12px;
    background: #e2e2e2;
    border-radius: 5px;
  }
`;
